
/**
 * Return the total price for a given plan and quantity, along with a breakdown
 * of how that total was calculated based on the plan's pricing scheme.
 * @param {*} plan subscription plan with potential tiers and pricing scheme
 * @param {*} quantity number of units for the subscription line
 * @param {*} schemeOverride optional parameter to override the plan's default pricing scheme (useful for previews)
 * @returns {*} [total, breakdown] where total is the calculated price and breakdown is an array of tier calculations
 */
export function pricingPreview (plan, quantity, schemeOverride = null) {
  const scheme = schemeOverride ?? (plan.tiers_mode || 'flat')
  const q = Number(quantity || 0)

  if (scheme === 'flat') {
    const total = Number(plan.amount || 0) * q
    return [total, [{ tierAmount: Number(plan.amount || 0), tierQuantity: q, tierTotal: total, kind: 'flat' }]]
  }

  if (scheme === 'volume') {
    let tier = plan.tiers.find(t => t.quantity_to != null && Number(t.quantity_from) <= q && Number(t.quantity_to) >= q)
    if (!tier) tier = plan.tiers[plan.tiers.length - 1]

    const unit = Number(tier.amount || 0)
    const total = unit * q
    return [total, [{ tierAmount: unit, tierQuantity: q, tierTotal: total, kind: 'volume' }]]
  }

  if (scheme === 'graduated') {
    let total = 0
    const breakdown = []

    for (const tier of plan.tiers) {
      const start = Math.max(1, Number(tier.quantity_from))

      if (start > q) {
        break
      }

      const end = Math.min(q, tier.quantity_to != null ? Number(tier.quantity_to) : q)

      if (end < start) {
        continue
      }

      const quantityInTier = end - start + 1
      const tierAmount = Number(tier.amount || 0)
      const amountForTier = (tier.amount_scheme === 'per_unit') ? quantityInTier * tierAmount : tierAmount

      breakdown.push({
        tierAmount,
        tierQuantity: quantityInTier,
        tierTotal: amountForTier,
        kind: 'graduated',
        from: start,
        to: end
      })
      total += amountForTier
    }

    return [total, breakdown]
  }

  if (scheme === 'package') {
    let tier = plan.tiers.find(t =>
      t.quantity_to != null &&
      Number(t.quantity_from) <= q &&
      Number(t.quantity_to) >= q
    )

    if (!tier) {
      tier = plan.tiers[plan.tiers.length - 1]
    }

    const tierAmount = Number(tier.amount || 0)
    return [tierAmount, [{ tierAmount, tierQuantity: 1, tierTotal: tierAmount, kind: 'package' }]]
  }

  if (scheme === 'package_overage') {
    let packageTier = plan.tiers
      .filter(t => t.quantity_to != null && t.unit_scheme !== 'overage')
      .find(t => Number(t.quantity_from) <= q && Number(t.quantity_to) >= q)

    if (!packageTier) {
      packageTier = plan.tiers
        .filter(t => t.quantity_to != null && t.unit_scheme !== 'overage')
        .sort((a, b) => Number(b.quantity_to) - Number(a.quantity_to))[0]
    }

    const overageTier = plan.tiers.find(t => t.unit_scheme === 'overage' || t.quantity_to == null)

    const packageAmount = Number(packageTier?.amount || 0)
    const baseMax = Number(packageTier?.quantity_to || 0)

    const overageQuantity = Math.max(0, q - baseMax)
    const overageUnitPrice = Number(overageTier?.amount || 0)
    const overageAmount = overageTier
      ? (overageTier.amount_scheme === 'per_unit' ? overageQuantity * overageUnitPrice : overageUnitPrice)
      : 0

    const breakdown = [
      { tierAmount: packageAmount, tierQuantity: 1, tierTotal: packageAmount, kind: 'package' },
      { tierAmount: overageUnitPrice, tierQuantity: overageQuantity, tierTotal: overageAmount, kind: 'overage' }
    ].filter(x => x.tierQuantity > 0)

    return [packageAmount + overageAmount, breakdown]
  }

  return [0, []]
}